import React, { useState, useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { HelpTooltip } from 'src/modules/automations/components/atoms/help-tooltip';
import { ToolCard } from 'src/modules/automations/components/molecules/tools/tool-card';
import { ToolConfig } from 'src/modules/automations/components/molecules/tools/tool-config';
import { ToolSearch } from 'src/modules/automations/components/molecules/tools/tool-search';
import { ToolsGridSkeleton } from 'src/modules/automations/components/molecules/tools/tools-grid-skeleton';
import type { Dictionary_unknown_ } from 'src/api/generated/models/Dictionary_unknown_';
import type { TCAction } from 'src/api/generated';
import type { JSONSchema7 } from 'json-schema';
import {
  EmployeeFormPath,
  toolConfigurationSchema,
} from 'src/modules/automations/components/organisms/employees/employee-form/types';
import type {
  EmployeeFormInputValues,
  EmployeeFormValues,
} from 'src/modules/automations/components/organisms/employees/employee-form/types';
import { useActions } from 'src/modules/shared/hooks/api/automations/actions';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { PiWrenchLight } from 'react-icons/pi';
import { z } from 'zod';
import {
  toSanitizedAlias,
  toDisplayAlias,
} from 'src/modules/automations/utils/tool-name';
import { cleanValueForSchema } from 'src/modules/shared/utils/json-schema.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { getToolMetadata } from 'src/modules/automations/utils/employees/employee-tool-presentation.util';

type ToolConfiguration = z.infer<typeof toolConfigurationSchema>;
const toolConfigurationListSchema = z.array(toolConfigurationSchema);

interface EmployeeToolsTabProps {
  form: UseFormReturn<EmployeeFormInputValues, unknown, EmployeeFormValues>;
  onToolAdded?: () => void;
}

const sanitizeConfiguredInputs = (
  schema: JSONSchema7 | undefined,
  inputs?: Dictionary_unknown_
): Dictionary_unknown_ => {
  const cleaned = cleanValueForSchema(schema, inputs);
  if (cleaned && typeof cleaned === 'object' && !Array.isArray(cleaned)) {
    return cleaned as Dictionary_unknown_;
  }
  return {};
};

export const EmployeeToolsTab: React.FC<EmployeeToolsTabProps> = ({ form }) => {
  const { t } = useI18n();
  const { watch, setValue } = form;
  const watchedTools = watch(EmployeeFormPath.DEFINITION_TOOLS);
  const employeeTools = useMemo<ToolConfiguration[]>(() => {
    const parsedTools = toolConfigurationListSchema.safeParse(
      watchedTools ?? []
    );
    if (parsedTools.success) return parsedTools.data;

    return [];
  }, [watchedTools]);

  const [newToolName, setNewToolName] = useState('');
  const [newToolInstructions, setNewToolInstructions] = useState('');
  const [newToolAlias, setNewToolAlias] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTool, setSelectedTool] = useState<TCAction | null>(null);
  const [configInputs, setConfigInputs] = useState<Dictionary_unknown_>({});
  const [showToolConfig, setShowToolConfig] = useState(false);
  const [aliasError, setAliasError] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const { actions: availableTools, loading: toolsLoading } = useActions();

  const filteredTools = useMemo(() => {
    if (!Array.isArray(availableTools) || availableTools.length === 0) {
      return [];
    }
    if (!searchQuery.trim()) return availableTools;

    return availableTools.filter((tool) => {
      const displayName = getToolMetadata(
        tool.name,
        t,
        tool.annotations?.title
      ).displayName;
      return (
        tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (tool.description?.toLowerCase().includes(searchQuery.toLowerCase()) ??
          false)
      );
    });
  }, [availableTools, searchQuery, t]);

  useEffect(() => {
    if (!selectedTool) {
      setConfigInputs({});
      setNewToolAlias('');
    }
    setAliasError('');
  }, [selectedTool]);

  const handleToolSelect = (tool: TCAction) => {
    setSelectedTool(tool);
    setNewToolName(tool.name);
    setNewToolInstructions('');
    setNewToolAlias('');
    setConfigInputs({});
    setIsEditing(false);
    setShowToolConfig(true);
  };

  const addTool = () => {
    if (!newToolName.trim() || !selectedTool) return;

    const currentTools: ToolConfiguration[] = [...employeeTools];
    const trimmedName = newToolName.trim();
    const trimmedAlias = newToolAlias.trim();
    const sanitizedAlias = toSanitizedAlias(trimmedAlias);
    const aliasPattern = /^[a-zA-Z0-9._-]+$/;

    if (sanitizedAlias && !aliasPattern.test(sanitizedAlias)) {
      setAliasError(
        // TODO: check when implementing i18n
        'Alias inválido: usa letras, números, punto, guion y guion bajo'
      );
      return;
    }
    const existingToolIndex = currentTools.findIndex(
      (tool) =>
        tool.name === trimmedName && (tool.alias ?? '') === sanitizedAlias
    );

    const existsWithSameBase = currentTools.some(
      (t, idx) => t.name === trimmedName && idx !== existingToolIndex
    );
    if (existsWithSameBase && sanitizedAlias === '') {
      setAliasError(
        // TODO: check when implementing i18n
        'Debes ingresar un alias para agregar múltiples configuraciones de la misma herramienta'
      );
      return;
    }
    if (
      sanitizedAlias &&
      currentTools.some(
        (t, idx) =>
          (t.alias ?? '') === sanitizedAlias && idx !== existingToolIndex
      )
    ) {
      // TODO: check when implementing i18n
      setAliasError(`El alias "${trimmedAlias}" ya está en uso`);
      return;
    }

    const inputSchema = selectedTool.inputSchema as JSONSchema7 | undefined;
    const sanitizedInputs = sanitizeConfiguredInputs(inputSchema, configInputs);
    const hasConfiguredInputs = Object.keys(sanitizedInputs).length > 0;

    const toolConfig: ToolConfiguration = {
      name: trimmedName,
      alias: sanitizedAlias.length > 0 ? sanitizedAlias : undefined,
      usageInstructions: newToolInstructions.trim() || undefined,
      configuredInputs:
        showToolConfig && hasConfiguredInputs ? sanitizedInputs : undefined,
    };

    const toolExists = existingToolIndex >= 0;
    if (toolExists) {
      currentTools[existingToolIndex] = toolConfig;
    } else {
      currentTools.push(toolConfig);
    }

    setValue(EmployeeFormPath.DEFINITION_TOOLS, currentTools, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    setNewToolName('');
    setNewToolInstructions('');
    setNewToolAlias('');
    setAliasError('');
    setSelectedTool(null);
    setConfigInputs({});
    setShowToolConfig(false);
  };

  const removeTool = (toolName: string, toolAlias?: string) => {
    const currentTools: ToolConfiguration[] = [...employeeTools];
    const filteredTools = currentTools.filter(
      (tool) =>
        !(tool.name === toolName && (tool.alias ?? '') === (toolAlias ?? ''))
    );

    setValue(EmployeeFormPath.DEFINITION_TOOLS, filteredTools, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const editTool = (toolName: string, toolAlias?: string) => {
    if (!Array.isArray(availableTools) || availableTools.length === 0) return;

    const tool = availableTools.find((t) => t.name === toolName);
    if (!tool) return;

    setSelectedTool(tool);
    setNewToolName(toolName);
    setIsEditing(true);

    const existingTool = employeeTools.find(
      (t) => t.name === toolName && (t.alias ?? '') === (toolAlias ?? '')
    );
    setNewToolInstructions(existingTool?.usageInstructions ?? '');
    const cleanedInputs = sanitizeConfiguredInputs(
      tool.inputSchema as JSONSchema7 | undefined,
      existingTool?.configuredInputs
    );
    setConfigInputs(cleanedInputs);
    setNewToolAlias(existingTool?.alias ?? '');
    setShowToolConfig(true);
  };

  const onCancelToolEdit = () => {
    setSelectedTool(null);
    setNewToolName('');
    setNewToolInstructions('');
    setNewToolAlias('');
    setAliasError('');
    setShowToolConfig(false);
  };

  const isEditingTool = employeeTools.some(
    (t) => t.name === newToolName && (t.alias ?? '') === newToolAlias
  );

  const handleConfigInputsChange = (data: Dictionary_unknown_) => {
    setConfigInputs(data);
  };

  const getToolDisplayName = (tool: ToolConfiguration): string => {
    if (tool.alias) return toDisplayAlias(tool.alias);

    const availableTool = availableTools.find((t) => t.name === tool.name);
    return getToolMetadata(tool.name, t, availableTool?.annotations?.title)
      .displayName;
  };

  const getToolSubtitle = (tool: ToolConfiguration): string | undefined => {
    if (!tool.alias) return;

    const availableTool = availableTools.find((t) => t.name === tool.name);
    return getToolMetadata(tool.name, t, availableTool?.annotations?.title)
      .displayName;
  };

  const toolConfigKey = useMemo(() => {
    if (!selectedTool) return 'none';
    // Include alias when editing so form remounts per configuration
    const mode = isEditing ? `edit:${newToolAlias || 'no-alias'}` : 'new';
    return `${selectedTool.name}:${mode}`;
  }, [selectedTool, isEditing, newToolAlias]);

  return (
    <div className="space-y-6 mt-6">
      <Panel variant="neutral" appearance="outline" className="pb-4">
        <PanelHeader size="sm" fixed>
          <div className="flex items-center gap-1">
            <Text size="h6" weight="medium">
              {/* TODO: check when implementing i18n */}
              Configuradas
            </Text>
            {/* TODO: check when implementing i18n */}
            <HelpTooltip content="Las herramientas que el agente puede utilizar. Configura cada herramienta con parámetros específicos." />
          </div>
        </PanelHeader>

        <div className="flex flex-col p-4 pb-0 max-h-[16rem] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {toolsLoading ? (
              <ToolsGridSkeleton count={4} />
            ) : employeeTools.length === 0 ? (
              <div className="col-span-full">
                <EmptyState
                  // TODO: check when implementing i18n
                  title="Tu agente IA no tiene herramientas configuradas"
                  icon={<PiWrenchLight className="h-4 w-4" />}
                  placement="horizontal"
                />
              </div>
            ) : (
              employeeTools.map((tool) => (
                <ToolCard
                  key={`${tool.name}:${tool.alias ?? ''}`}
                  name={getToolDisplayName(tool)}
                  subtitle={getToolSubtitle(tool)}
                  instructions={tool.usageInstructions}
                  configuredInputsCount={
                    Object.keys(tool.configuredInputs ?? {}).length
                  }
                  onEdit={() => editTool(tool.name, tool.alias)}
                  onRemove={() => removeTool(tool.name, tool.alias)}
                />
              ))
            )}
          </div>
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel
          variant="neutral"
          appearance="outline"
          className={!selectedTool ? 'lg:col-span-2' : ''}
        >
          <PanelHeader size="sm" fixed>
            <Text size="h6" weight="medium">
              {/* TODO: check when implementing i18n */}
              Disponibles
            </Text>
          </PanelHeader>
          <div className="flex flex-col p-4 max-h-[22rem] overflow-y-auto">
            <ToolSearch
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filteredTools={filteredTools}
              isLoading={toolsLoading}
              onSelectTool={handleToolSelect}
              selectedTool={selectedTool}
            />
          </div>
        </Panel>

        {selectedTool && (
          <Panel variant="neutral" appearance="outline">
            <PanelHeader
              size="sm"
              fixed
              actions={
                <>
                  <Button
                    onClick={onCancelToolEdit}
                    variant="neutral"
                    appearance="ghost"
                    size="sm"
                  >
                    <Text size="small">
                      {/* TODO: check when implementing i18n */}
                      Cancelar
                    </Text>
                  </Button>
                  <Button
                    onClick={() => {
                      addTool();
                    }}
                    variant="primary"
                    size="sm"
                  >
                    <Text size="small">
                      {/* TODO: check when implementing i18n */}
                      {isEditingTool
                        ? 'Actualizar herramienta'
                        : 'Agregar herramienta'}
                    </Text>
                  </Button>
                </>
              }
            >
              <div className="flex items-center justify-between">
                <Text size="h6" weight="medium">
                  {
                    getToolMetadata(
                      selectedTool.name,
                      t,
                      selectedTool.annotations?.title
                    ).displayName
                  }
                </Text>
              </div>
            </PanelHeader>

            <ToolConfig
              selectedTool={selectedTool}
              instructions={newToolInstructions}
              setInstructions={setNewToolInstructions}
              showConfigPanel={showToolConfig}
              configuredInputs={configInputs}
              handleConfigInputsChange={handleConfigInputsChange}
              alias={newToolAlias}
              setAlias={(value) => {
                setAliasError('');
                setNewToolAlias(value);
              }}
              aliasError={aliasError}
              showAliasField={true}
              formKey={toolConfigKey}
            />
          </Panel>
        )}
      </div>
    </div>
  );
};
